import { ActionContext, ActionTree } from 'vuex'

import {
  DashboardApiRecord,
  DashboardTemplate,
  Dictionary,
  Message,
  MessagePullApiRequest,
  MessagePullApiRequestMessageChannel,
  StateInterface,
  VehicleStatusHistory
} from './state.interface'

import { ChatService } from 'messenger/chat/ChatService'
import { ApiRequestService, ApiUrls } from '../services/ApiRequestService'

import {
  SET_ALARM,
  SET_ALARM_DASHBOARD_TEMPLATE,
  SET_APP_DARKMODE,
  SET_APP_INITIALIZED,
  SET_AUX_NAV_TEMPLATE,
  SET_CLUSTER,
  SET_CURRENT_DASHBOARD_SETTINGS,
  SET_DASHBOARD,
  SET_DM,
  SET_EVENTS,
  SET_JWT,
  SET_LOCALMONITOR,
  SET_MESSAGE,
  SET_MESSAGE_CHANNEL,
  SET_MONITOR,
  SET_MONITOR_SORTED_QUALIFICATIONS,
  SET_NAV_CUSTOM_TEMPLATE,
  SET_NEWS,
  SET_REMOVE_CHANNEL_FROM_COLLECTION,
  SET_SELECTED_CHANNEL_ID,
  SET_SELECTED_LAYOUT_INDEX,
  SET_STATUS,
  SET_STATUSPLAN,
  SET_STORE_MESSAGE,
  SET_UCR,
  SET_UCR_ACTIVE,
  SET_UCR_DEFAULT,
  SET_USER,
  SET_VEHICLE_STATUS
} from './mutations'

export const keyActionMapping: Record<'alarm' | 'cluster' | 'dashboard' | 'dm' | 'events' | 'localmonitor' | 'message'
| 'message_channel' | 'monitor' | 'news' | 'status' | 'statusplan' | 'ucr' | 'ucr_default' | 'ucr_active' | 'user' | string, string> = {
  alarm: 'setAlarmState',
  cluster: 'setClusterState',
  dashboard: 'setDashboardState',
  dm: 'setDMState',
  events: 'setEventsState',
  localmonitor: 'setLocalmonitorState',
  message: 'setMessageState',
  message_channel: 'setMessageChannelState',
  monitor: 'setMonitorState',
  news: 'setNewsState',
  status: 'setStatusState',
  statusplan: 'setStatusplanState',
  ucr: 'setUCRState',
  ucr_default: 'setUCRDefaultState',
  ucr_active: 'setUCRActiveState',
  user: 'setUserState'
}

export const actions: ActionTree<StateInterface, any> = {
  /*** New Actions ***/
  async getAllPullData({ commit }: ActionContext<StateInterface, any>) {
    const response = await ApiRequestService.get(ApiUrls.pull.all)
    const { data } = response.data

    commit(SET_ALARM, data.alarm)
    commit(SET_CLUSTER, data.cluster)
    commit(SET_EVENTS, data.events)
    commit(SET_MESSAGE, data.message)
    commit(SET_MESSAGE_CHANNEL, data.message_channel)
    commit(SET_NEWS, data.news)
    commit(SET_STATUS, data.status)
    commit(SET_UCR, data.ucr)
    commit(SET_USER, data.user)
  },
  async setAlarmState({ state, commit }: ActionContext<StateInterface, any>, alarmPayload: object | any) {
    if (Object.keys(alarmPayload.items).length !== alarmPayload.sorting.length) {
      const currentAlarms = state.alarm

      let diff: number[] = currentAlarms.sorting.filter((elem: number) => !alarmPayload.sorting.includes(elem))

      for (const key of diff) {
        delete currentAlarms.items[key]
      }

      alarmPayload.items = currentAlarms.items
    }

    commit(SET_ALARM, alarmPayload)
  },
  async setClusterState({ commit }: ActionContext<StateInterface, any>, clusterPayload: object) {
    commit(SET_CLUSTER, clusterPayload)
  },
  async setDashboardState({ commit }: ActionContext<StateInterface, any>, dashboardPayload: object) {
    commit(SET_DASHBOARD, dashboardPayload)
  },
  async setDMState({ commit }: ActionContext<StateInterface, any>, dmPayload: object) {
    commit(SET_DM, dmPayload)
  },
  async setEventsState({ commit }: ActionContext<StateInterface, any>, eventsPayload: object) {
    commit(SET_EVENTS, eventsPayload)
  },
  async setLocalmonitorState({ commit }: ActionContext<StateInterface, any>, localmonitorPayload: object) {
    commit(SET_LOCALMONITOR, localmonitorPayload)
  },
  async setMessageState({ commit }: ActionContext<StateInterface, any>, messagePayload: object) {
    commit(SET_MESSAGE, messagePayload)
  },
  async setMessageChannelState({ commit }: ActionContext<StateInterface, any>, messageChannelPayload: object) {
    commit(SET_MESSAGE_CHANNEL, messageChannelPayload)
  },
  async setMonitorState({ commit }: ActionContext<StateInterface, any>, monitorPayload: object) {
    commit(SET_MONITOR, monitorPayload)
  },
  async setNewsState({ commit }: ActionContext<StateInterface, any>, newsPayload: object) {
    commit(SET_NEWS, newsPayload)
  },
  async setStatusState({ commit }: ActionContext<StateInterface, any>, statusPayload: object) {
    commit(SET_STATUS, statusPayload)
  },
  async setStatusplanState({ commit }: ActionContext<StateInterface, any>, statusplanPayload: object) {
    commit(SET_STATUSPLAN, statusplanPayload)
  },
  async setUCRState({ commit }: ActionContext<StateInterface, any>, ucrPayload: object) {
    commit(SET_UCR, ucrPayload)
  },
  async setUCRDefaultState({ commit }: ActionContext<StateInterface, any>, ucrDefaultPayload: number) {
    commit(SET_UCR_DEFAULT, ucrDefaultPayload)
  },
  async setUCRActiveState({ commit }: ActionContext<StateInterface, any>, ucrActivePayload: number) {
    commit(SET_UCR_ACTIVE, ucrActivePayload)
  },
  async setUserState({ commit }: ActionContext<StateInterface, any>, userPayload: object) {
    commit(SET_USER, userPayload)
  },
  async setJWT({ commit }: ActionContext<StateInterface, any>, jwtPayload: object) {
    commit(SET_JWT, jwtPayload)
  },
  async setAppInitialized({ commit }: ActionContext<StateInterface, any>, appInitializedPayload: boolean) {
    commit(SET_APP_INITIALIZED, appInitializedPayload)
  },
  async setAppDarkMode({ commit }: ActionContext<StateInterface, any>, appDarkModePayload: boolean) {
    commit(SET_APP_DARKMODE, appDarkModePayload)
  },
  async setMonitorSortedQualifications({ commit }: ActionContext<StateInterface, any>, monitorSortedQualificationsPayload: Array<any>) {
    commit(SET_MONITOR_SORTED_QUALIFICATIONS, monitorSortedQualificationsPayload)
  },
  async setAlarmDashboardTemplate({ commit }: ActionContext<StateInterface, any>, alarmDashboardTemplatePlayload) {
    commit(SET_ALARM_DASHBOARD_TEMPLATE, alarmDashboardTemplatePlayload)
  },
  async setCurrentDashboardSettings({ commit }: ActionContext<StateInterface, any>, currentDashboardSettingsPayload: object) {
    commit(SET_CURRENT_DASHBOARD_SETTINGS, currentDashboardSettingsPayload)
  },
  async setNavCustomTemplate({ commit }: ActionContext<StateInterface, any>, navCustomTemplatePayload) {
    commit(SET_NAV_CUSTOM_TEMPLATE, navCustomTemplatePayload)
  },
  async setAuxNavTemplate({ commit }: ActionContext<StateInterface, any>, auxNavTemplatePayload) {
    commit(SET_AUX_NAV_TEMPLATE, auxNavTemplatePayload)
  },

  async setVehicleStatus({ commit }: ActionContext<StateInterface, any>, vehicleStatusPayload: object) {
    commit(SET_VEHICLE_STATUS, vehicleStatusPayload)
  },
  async setVehicleStatusHistory({ commit }: ActionContext<StateInterface, any>, vehicleStatusHistoryPayload: object) {
    commit('processVehicleStatusLog', vehicleStatusHistoryPayload)
  },

  /*** MESSENGER specific ***/
  async setStoreMessage({ commit }: ActionContext<StateInterface, any>, storeMessagePayload: object) {
    commit(SET_STORE_MESSAGE, storeMessagePayload)
  },
  async setRemoveChannelFromCollection({ commit }: ActionContext<StateInterface, any>, removeChannelFromCollectionPayload: object) {
    commit(SET_REMOVE_CHANNEL_FROM_COLLECTION, removeChannelFromCollectionPayload)
  },
  async setSelectedChannelId({ commit }: ActionContext<StateInterface, any>, selectedChannelIdPayload: object) {
    commit(SET_SELECTED_CHANNEL_ID, selectedChannelIdPayload)
  },
  async setSelectedLayoutIndex({ commit }: ActionContext<StateInterface, any>, selectedLayoutIndexPayload: object) {
    commit(SET_SELECTED_LAYOUT_INDEX, selectedLayoutIndexPayload)
  },
  /*** Legacy Actions ***/
  async processMessages({ commit, dispatch }: ActionContext<StateInterface, any>, messages: Dictionary<Message>) {
    const affectedChannelIds: number[] = []

    for (let id in messages) {
      if (!messages.hasOwnProperty(id)) continue

      const message = messages[id]
      const channelId = message.message_channel_id

      await dispatch('processMessage', { message: message })

      if (affectedChannelIds.indexOf(channelId) === -1) {
        affectedChannelIds.push(channelId)
      }
    }

    affectedChannelIds.forEach(channelId => commit('sortChannelMessages', channelId))
  },
  async validateMessageCache({ commit, dispatch }: ActionContext<StateInterface, any>, data: MessagePullApiRequest) {
    const channels = data.message_channel

    const parents = data.parents
    for (const parentId in parents) {
      if (!parents.hasOwnProperty(parentId)) continue
      commit(SET_STORE_MESSAGE, parents[parentId])
    }

    for (const channelId in channels) {
      if (!channels.hasOwnProperty(channelId)) continue

      const channel: MessagePullApiRequestMessageChannel = channels[channelId]

      if (channel.drop) {
        await dispatch('pruneChannelCache', { channelId: channelId as unknown as number, deleteBeforeMessageId: channel.drop_before_message_id })
      }

      let i = 0
      const allMessages = [...channel.messages_new, ...channel.messages_updated]
      const numMessages = allMessages.length

      for (; i < numMessages; i++) {
        const messageId = allMessages[i]
        const message: Message = data.items[messageId]

        if (!message) continue

        await dispatch('processMessage', { message: message })
      }
    }
  },
  async moveChannelToColumn({ commit }: ActionContext<StateInterface, any>, payload: { columnId: number, indexInColumn: number, channel: MessageChannel }) {
    let { columnId, indexInColumn, channel } = payload

    // Remove dragged element from column
    commit(SET_REMOVE_CHANNEL_FROM_COLLECTION, channel)

    // Add channel at new position
    commit('addChannelToCollection', {
      columnId: columnId,
      indexInColumn: indexInColumn,
      channel: channel
    })
  },
  async loadDashboardLayouts({ commit }: ActionContext<StateInterface, any>) {
    const reply = await ApiRequestService.get(ApiUrls.dashboard.base + '?template_type=1')

    if (!reply || (reply.status !== 200) || !reply.data || !reply.data.success || !reply.data.data) {
      console.error('Store.loadDashboardLayouts()', 'api request failed')
      return
    }

    const templates: DashboardTemplate[] = []
    const items = reply.data.data.items

    for (let id in items) {
      if (!items.hasOwnProperty(id)) return

      const data = items[id]
      const template: DashboardTemplate = data.config
      template.id = data.id
      templates.push(template)
    }

    commit('setDashboardTemplates', templates)
  },
  async createDashboardTemplate({ commit, dispatch }: ActionContext<StateInterface, any>, template: DashboardTemplate) {
    const data: DashboardApiRecord = {
      template_type: 1,
      access_type: 1,
      name: template.title,
      config: template
    }
    const reply = await ApiRequestService.post(ApiUrls.dashboard.base, { Dashboard: data })

    if (!reply || (reply.status !== 200) || !reply.data || !reply.data.success || !reply.data.data) {
      console.error('Store.createDashboardTemplate()', 'api request failed')
      return
    }

    template.id = reply.data.data.id

    commit('addDashboardTemplate', template)
    await dispatch('selectDashboardTemplate', template)
  },
  async loadDashboardTemplate({ commit }: ActionContext<StateInterface, any>, templateId: number) {
    if (!templateId) {
      console.error('Store.loadDashboardTemplate()', 'no id provided')
      return
    }

    const apiURL = ApiUrls.dashboard.base + '/' + templateId
    let reply = await ApiRequestService.get(apiURL)

    if (!reply || (reply.status !== 200) || !reply.data || !reply.data.success || !reply.data.data) {
      console.error('Store.loadDashboardTemplate()', 'api request failed')
      return
    }

    const data = reply.data.data
    const template: DashboardTemplate = data.config
    template.id = data.id

    commit('addDashboardTemplate', template)
  },
  async saveDashboardTemplate({ commit, dispatch }: ActionContext<StateInterface, any>, template: Partial<DashboardTemplate>) {
    if (!template.id) {
      console.error('Store.saveDashboardTemplate()', 'no id provided')
      return
    }

    const apiURL = ApiUrls.dashboard.base + '/' + template.id
    let reply = await ApiRequestService.get(apiURL)

    if (!reply || (reply.status !== 200) || !reply.data || !reply.data.success || !reply.data.data) {
      console.error('Store.saveDashboardTemplate()', 'api request failed')
      return
    }

    let data: DashboardApiRecord = reply.data.data
    const origTemplate: DashboardTemplate = data.config

    origTemplate.id = data.id

    for (let prop in template) {
      if (!template.hasOwnProperty(prop) || (prop === 'id')) continue

      origTemplate[prop] = template[prop]
    }

    data = {
      template_type: 1,
      access_type: 1,
      name: origTemplate.title,
      config: origTemplate
    }
    reply = await ApiRequestService.put(apiURL, { Dashboard: data })

    if (!reply || (reply.status !== 200) || !reply.data || !reply.data.success || !reply.data.data) {
      console.error('Store.saveDashboardTemplate()', 'api request failed')
      return
    }

    commit('addDashboardTemplate', origTemplate)
  },
  async deleteDashboardTemplate({ state, commit, dispatch }: ActionContext<StateInterface, any>, dashboardId: number) {
    const reply = await ApiRequestService.delete(ApiUrls.dashboard.base + '/' + dashboardId)

    if (!reply || (reply.status !== 200) || !reply.data || !reply.data.success) {
      console.error('Store.deleteDashboardTemplate()', 'api request failed')
      return
    }

    if (state.dashboard.selectedTemplate && state.dashboard.selectedTemplate.id === dashboardId) {
      await dispatch('selectDashboardTemplate', null)
    }

    commit('removeDashboardTemplate', dashboardId)
  },
  async selectDashboardTemplate({ commit, dispatch }: ActionContext<StateInterface, any>, template: DashboardTemplate | null) {
    commit('setSelectedDashboardTemplate', template)
  },
  async saveNewsSurveyReply({ commit, dispatch }: ActionContext<StateInterface, any>, { newsID, surveyID, answerID, checked }) {
    const apiURL = ApiUrls.news.confirm + '/' + newsID
    let reply = await ApiRequestService.post(apiURL, {
      NewsSurvey: {
        id: surveyID
      },
      NewsSurveyAnswer: {
        id: answerID,
        checked: checked
      }
    })

    if (!reply || (reply.status !== 200) || !reply.data || !reply.data.success || !reply.data.data) {
      console.error('Store.saveNewsSurveyReply()', 'api request failed')
      return
    }

    commit('updateNewsItem', reply.data.data)
  },
  async saveNewsSurveyCustomReply({ commit, dispatch }: ActionContext<StateInterface, any>, { newsID, surveyID, customAnswer }) {
    const apiURL = ApiUrls.news.confirm + '/' + newsID
    let reply = await ApiRequestService.post(apiURL, {
      NewsSurvey: {
        id: surveyID
      },
      NewsSurveyAnswer: {
        id: 0,
        custom_answer: customAnswer
      }
    })

    if (!reply || (reply.status !== 200) || !reply.data || !reply.data.success || !reply.data.data) {
      console.error('Store.saveNewsSurveyReply()', 'api request failed')
      return
    }

    commit('updateNewsItem', reply.data.data)
  },
  async saveEventParticipation({ commit, dispatch }: ActionContext<StateInterface, any>, { eventID, choice }) {
    const apiURL = ApiUrls.events.confirm + '/' + eventID
    let reply = await ApiRequestService.post(apiURL, {
      Event: {
        participation: choice
      }
    })

    if (!reply || (reply.status !== 200) || !reply.data || !reply.data.success || !reply.data.data) {
      console.error('Store.saveEventParticipation()', 'api request failed')
      return
    }

    commit('updateEventItem', reply.data.data)
  },
  async saveEventCustomReply({ commit, dispatch }: ActionContext<StateInterface, any>, { eventID, customAnswer }) {
    const apiURL = ApiUrls.events.confirm + '/' + eventID
    let reply = await ApiRequestService.post(apiURL, {
      Event: {
        participation: 0,
        custom_answer: customAnswer
      }
    })

    if (!reply || (reply.status !== 200) || !reply.data || !reply.data.success || !reply.data.data) {
      console.error('Store.saveEventCustomReply()', 'api request failed')
      return
    }

    commit('updateEventItem', reply.data.data)
  },
  async saveAlarmStatus({ commit }: ActionContext<StateInterface, any>, { alarmID, statusID, customAnswer }) {
    const apiURL = ApiUrls.alarms.confirm + '/' + alarmID
    let reply = await ApiRequestService.post(apiURL, {
      Status: {
        id: statusID,
        note: customAnswer
      }
    })

    if (!reply || (reply.status !== 200) || !reply.data || !reply.data.success || !reply.data.data) {
      console.error('Store.saveAlarmCustomReply()', 'api request failed')
      return
    }

    commit('updateAlarmItem', reply.data.data)
  },
  async loadVehicleStatusHistory({ commit, dispatch, state }: ActionContext<StateInterface, any>) {
    if (state.vehicle_status_history.isInitialised) return

    const reply = await ApiRequestService.get(ApiUrls.usingVehicles.getStatusLog + '?ts=' + state.vehicle_status_history.ts)

    if (!reply || (reply.status !== 200) || !reply.data || !reply.data.success || !reply.data.data) {
      console.error('Store.loadVehicleStatusHistory()', 'api request failed')
      return
    }

    const vehicleHistory: VehicleStatusHistory = {}

    reply.data.data.items.forEach((log: any) => {
      const vehicleId = log.id
      if (!vehicleHistory[vehicleId]) {
        vehicleHistory[vehicleId] = []
      }
      vehicleHistory[vehicleId].push(log)
    })

    commit('initialiseVehicleStatusHistory', { items: vehicleHistory, ts: reply.data.data.ts })
  },
  async markChannelAsRead({ commit, dispatch, state }: ActionContext<StateInterface, any>, channelId: number) {
    const channel = state.message_channel.items[channelId]
    const channelDetails = state.messenger.channels[channelId]

    if (!channel || !channelDetails) return

    const selfLastMessageId = channelDetails.last_message_id

    if (!selfLastMessageId) return

    commit('updateChannelLastMessageDetails', {
      channelId: channelId,
      lastMessageId: channel.last_message_id,
      tsLastMessage: channel.ts_last_message,
      selfLastMessageId: selfLastMessageId
    })

    const reply = await ApiRequestService.post(ApiUrls.messageChannel.activity + '/' + channelId, {
      'MessageChannel': {
        'last_message_id': selfLastMessageId
      }
    })

    if (!reply || (reply.status !== 200) || !reply.data || !reply.data.success) {
      console.error('Store.markChannelAsRead()', 'api request failed')
      return
    }
  },
  async processMessage({ commit, state }: ActionContext<StateInterface, any>, { message, isSocketEvent }) {
    const channelId = message.message_channel_id
    const channelData = state.message_channel.items[channelId]
    const lastChannelMessageId = channelData.last_message_id

    commit('updateMessage', message)

    // if we didn't receive this message via web-socket then we don't need to check if we need to automatically mark the channel as read
    if (!isSocketEvent) return

    const messageId = message.id
    const channelIsOpened = ChatService.openedChannels.indexOf(channelId) > -1
    const markAsRead = channelIsOpened && channelData && (messageId > lastChannelMessageId) && (message.author_id !== state.ucr_active)

    if (markAsRead) {
      const reply = await ApiRequestService.post(ApiUrls.messageChannel.activity + '/' + channelId, {
        'MessageChannel': {
          'last_message_id': messageId
        }
      })
    }
  },
  async pruneChannelCache({ commit, state }: ActionContext<StateInterface, any>, { channelId, deleteBeforeMessageId }) {
    if (!state.messenger || !state.messenger.channels || !state.messenger.channels[channelId] || !deleteBeforeMessageId) {
      return
    }

    const channel = state.messenger.channels[channelId]
    const messageIdsToDelete = channel.messageSorting.filter(messageId => messageId < deleteBeforeMessageId)

    commit('deleteMessages', { messageIds: messageIdsToDelete })
  }
}

export default actions
